import Vue from 'vue';
import { useActions } from 'vuex-composition-helpers/dist';
import {
  isPlainObject,
  cloneDeep,
} from 'lodash';
import { validate as veeValidate } from 'vee-validate';
import {
  reactive,
  ref,
  computed,
  inject,
} from '@vue/composition-api';

import useProcessing from '@/composables/useProcessing';
import { handleValidationError } from '@/helpers/bugsnag';

export default (props, { root }) => {
  const config = inject('config');
  const constants = inject('constants');
  const formMetadata = inject('formMetadata');
  const productVersion = inject('productVersion');
  const productName = inject('productName');

  const form = ref(null);

  const {
    isProcessing,
    toggleProcessing,
  } = useProcessing();

  const { showToast } = useActions(['showToast']);

  // reactive
  const journey = ref({});
  const payload = reactive({});
  const logos = ref([]);

  // refs
  const isValidated = ref(false);
  const formActions = ref(null);
  const step = ref(null);
  const recaptcha = ref(null);
  const rules = ref(null);
  const version = ref(null);
  const isLoading = ref(false);
  const isEditProcess = ref(false);
  const quoteLimitExceeded = ref(false);
  const isReturningUser = ref(false);
  const isConfirmStepsShow = ref(true);
  const isContinueTriggered = ref(false);
  const stepsObservers = ref(formMetadata.steps.reduce((acc, step) => ({
    ...acc,
    [step.stepId]: null,
  }), {}));
  const stepsPassed = ref(formMetadata.steps.reduce((acc, step, index) => ({
    ...acc,
    [step.stepId]: false,
  }), {}));

  const payloadRefined = computed(() => {
    const refined = {};
    Object.keys(payloadRefined).forEach(key => {
      if (
        [
          'telephone',
          'secondaryTelephone',
        ].includes(key)
      ) {
        refined[key] = payload[key].replace('+44', '0');
      } else {
        refined[key] = payload[key];
      }
    });
    return refined;
  });
  const showCaptcha = computed(() => process.env.NODE_ENV === 'production' ? !config.disableCaptcha : false);
  const fixStickyContainer = () => {
    const event = document.createEvent('Event');
    event.initEvent('resize', false, true);
    window.dispatchEvent(event);
  };

  const handleEditMultiform = async () => {
    toggleProcessing(false);
    isContinueTriggered.value = true;
  };

  const onValid = async () => {
    await Vue.nextTick();
    fixStickyContainer();
  };

  const validateForm = async (formObserver) => {
    form.value.$refs[`step-${formMetadata.steps.slice().pop().stepId}`][0].$setStepValidated();

    const isValid = await formObserver.validate();

    isValidated.value = true;

    // if form is not valid, scroll to the form errors section
    if (!isValid) {
      await Vue.nextTick();

      const failedStep = formMetadata.steps.find(({ stepId }) => {
        return stepsObservers.value[stepId].failed;
      });

      root.$scrollTo(`#form-errors-${failedStep.stepId}`, 300);

      showToast({
        type: 'warning',
        text: 'Please fix all errors to continue',
      });
    }

    return isValid;
  };

  const getQuotes = async (formObserver) => {
    toggleProcessing(true);
    const isValid = await validateForm(formObserver);

    if (!isValid) {
      toggleProcessing(false);
      return;
    }

    // if multiform in edit process, inside mutiformController re-run validation and if form is valid save data
    // after that allow user go to the result page
    if (isEditProcess.value) {
      await handleEditMultiform();
      await getQuotes(formObserver);
      return;
    }

    const SID = await submit();

    if (SID) {
      await root.$router.push({
        name: `${productName}-insurance-results`,
        params: { sid: SID },
      }, () => {});
    }

    toggleProcessing(false);
  };

  const loadJourney = async () => {
    let journey = null;
    try {
      journey = await root.$api.showJourney({
        productName,
        productVersion,
        productPartnerId: config.partner,
      });
    } catch (error) {
      console.log(error.toString());
    }
    return journey;
  };

  const submit = async () => {
    let SID = false;
    try {
      const response = await root.$api.getLgQuote({
        productName,
        quoteData: payload,
        productVersion,
        productPartnerId: config.partner === 'quotezonenew' ? constants.DEFAULT_PARTNER : config.partner,
      },
      );
      const { status } = response;

      if (status === 'success') {
        const { data } = response;
        SID = data.SID;
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        handleValidationError(error);
      }

      showToast({
        type: 'error',
        text: 'Unexpected error',
      });

      if (error?.response?.data?.errors) {
        console.log(JSON.stringify(error.response.data.errors, null, 2));
        if (error.response.data.errors === 'Too many requests') {
          if (config.partnerQuoteLimitAction === 'redirect') {
            // or change window location
            window.location = config.partnerQuoteLimitData;
            return;
          }
          // show quote limit dialog and prevent further progress + reset captcha
          quoteLimitExceeded.value = true;
          recaptcha.value && recaptcha.value.reset();
          return;
        }
        alert(JSON.stringify(error.response.data.errors, null, 2));
      }
    }

    return SID;
  };

  // recaptcha
  const validateBeforeRecaptcha = async (formObserver) => {
    toggleProcessing(true);

    const isValid = await validateForm(formObserver);

    if (!isValid) {
      toggleProcessing(false);
      return;
    }

    // if multiform in edit process, inside mutiformController re-run validation and if form is valid save data
    // after that allow user go to the result page
    if (isEditProcess.value) {
      await handleEditMultiform();
      await validateBeforeRecaptcha(formObserver);
      return;
    }

    // if form valid exec recaptcha
    recaptcha.value.execute();
    toggleProcessing(false);
  };

  const transformArrayToObj = array => array.reduce((acc, field) => ({
    ...acc,
    [field]: field,
  }), {});

  const prevalidateFieldValue = async (sessionValue, sessionData, prevalidationRules, validationRules) => {
    let rules = null;

    if (prevalidationRules) {
      if (isPlainObject(prevalidationRules)) {
        rules = prevalidationRules;
      } else if (
        prevalidationRules === true &&
        isPlainObject(validationRules)
      ) {
        rules = validationRules;
      }
    }

    if (!rules) return true;

    if (isPlainObject(rules)) {
      const { valid } = await veeValidate(sessionValue, rules, { values: sessionData });
      return valid;
    }
    if (Array.isArray(rules)) {
      const validity = await Promise.all(
        rules.map(rulesSet => veeValidate(sessionValue, rulesSet, { values: sessionData })),
      );
      return !validity.some(({ valid }) => valid === false);
    }
  };

  const prevalidate = async data => {
    const result = cloneDeep(data);
    await Promise.all(
      formMetadata.steps.map(async ({ stepId, questions }) => {
        return await Promise.all(
          questions.map(async ({ params: { customFields = {}, prevalidationRules = {}, validationRules = {} } }) => {
            const customFieldsObj = Array.isArray(customFields) ? transformArrayToObj(customFields) : customFields;
            return await Promise.all(
              Object.keys(customFieldsObj).map(async fieldName => {
                const fieldKey = customFieldsObj[fieldName];
                if (Object.prototype.hasOwnProperty.call(data, fieldKey)) {
                  const sessionValue = formMetadata.singleStep ? data[fieldKey] : data[stepId][fieldKey];
                  const isValid = await prevalidateFieldValue(sessionValue, data, prevalidationRules[fieldName], validationRules[fieldName]);
                  if (!isValid) {
                    if (formMetadata.singleStep) result[fieldKey] = null;
                    else result[stepId][fieldKey] = null;
                  }
                }
              }),
            );
          }),
        );
      }),
    );
    return result;
  };

  const init = async () => {
    isLoading.value = true;

    const [
      journeyResponse,
      logosResponse,
    ] = await Promise.all([
      loadJourney(),
      root.$api.getLgLogos(),
    ]);

    if (journeyResponse.status === 'success') {
      const { data } = journeyResponse;

      journey.value = await prevalidate(data);

      isReturningUser.value = data?.returningUser || null;

      isConfirmStepsShow.value = !data.quoteEngineQuote && !!formMetadata?.confirmSteps?.length;

      version.value = data.productVersion;
    }

    if (logosResponse.status === 'success') {
      logos.value = Array.from(new Set(logosResponse.data));
    }

    // Needed for development purposes - increases phpsessid lifetime
    if (process.env.NODE_ENV === 'development') {
      setInterval(loadJourney, 30 * 60 * 1000);
    }

    isLoading.value = false;
  };

  return {
    // main data
    payload,
    payloadRefined,
    journey,
    logos,

    // states
    isLoading,
    isReturningUser,
    isConfirmStepsShow,
    quoteLimitExceeded,

    // computed
    showCaptcha,

    // methods
    onValid,
    getQuotes,
    init,
    validateBeforeRecaptcha,

    // other
    isProcessing,
    isEditProcess,
    isContinueTriggered,

    // refs
    isValidated,
    formActions,
    step,
    recaptcha,
    rules,
    stepsObservers,
    stepsPassed,

    form,
  };
};
